import { roundToTwoDecimals } from 'common/helpers/';
import {
  estimateCallDurationSeconds,
  toFiniteNumber,
  toPercent,
} from 'features/analytics/helpers/callScoreCurves';
import {
  scoreCallDirections,
  type CallDirectionScores,
  type DirectionalPathQuality,
} from 'features/analytics/helpers/scoreCallDirections';
import type { CallStats } from 'features/analytics/interfaces/';

export type QualityFaultSource =
  'none' | 'station' | 'endpoint' | 'both' | 'indeterminate';

export type StationQualityFactor =
  | 'sent_path_degraded'
  | 'sent_video_disruptions'
  | 'received_freezes'
  | 'cpu_limited'
  | 'bandwidth_limited'
  | 'slow_decode'
  | 'low_camera_framerate'
  | 'tcp_relay';

export type EndpointQualityFactor =
  'received_path_degraded' | 'received_freezes' | 'low_received_framerate';

export interface StationDeviceMetrics {
  cpuLimitedPercent?: number;
  bandwidthLimitedPercent?: number;
  avgDecodeTimeMs?: number;
  cameraFps?: number;
  keyframeRequestsPerMinute?: number;
  localCandidateType?: string;
  localRelayProtocol?: string;
}

export interface CallQualityAttribution {
  likelySource: QualityFaultSource;
  /** Same as likelySource, except `both` is narrowed by severity when one side clearly dominates. */
  primarySource: QualityFaultSource;
  stationFactors: StationQualityFactor[];
  endpointFactors: EndpointQualityFactor[];
  station: StationDeviceMetrics;
}

// Tuned for interpreting: smooth, freeze-free video on both sides is required for comprehension.
const THRESHOLDS = {
  degradedPathScore: 85,
  cpuLimitedPercent: 5,
  // Every call starts bandwidth-limited while BWE ramps up, so this can't go much lower.
  bandwidthLimitedPercent: 15,
  avgDecodeTimeMs: 20,
  lowFps: 20,
  // End of the "Good" QoE freeze band.
  freezesPerMinute: 0.15,
  keyframeRequestsPerMinute: 1,
  // A few keyframe requests at call setup are normal.
  minKeyframeRequests: 3,
  // Direction score gap at which the worse direction's sender is treated as the primary cause.
  primaryDirectionScoreMargin: 10,
};

// A station that can't keep up is directly measured evidence, stronger than inferred network blame.
const STATION_OVERLOAD_FACTORS = new Set<StationQualityFactor>([
  'cpu_limited',
  'slow_decode',
]);

const perMinute = (
  count: number | undefined,
  durationSeconds: number
): number | undefined =>
  count !== undefined && durationSeconds > 0
    ? roundToTwoDecimals(count / (durationSeconds / 60))
    : undefined;

const getCallDurationSeconds = (summary: CallStats): number =>
  estimateCallDurationSeconds(
    toFiniteNumber(summary.inboundRtpVideo?.framesDecoded),
    toFiniteNumber(summary.inboundRtpVideo?.framesReceived),
    toFiniteNumber(summary.inboundRtpVideo?.framesPerSecond)
  );

const getKeyframeRequestCount = (summary: CallStats): number | undefined => {
  const pli = toFiniteNumber(summary.outboundRtpVideo?.pliCount);
  const fir = toFiniteNumber(summary.outboundRtpVideo?.firCount);
  return pli === undefined && fir === undefined
    ? undefined
    : (pli ?? 0) + (fir ?? 0);
};

const getLimitationPercent = (
  durations: Record<string, number> | undefined,
  reason: string
): number | undefined => {
  if (!durations) {
    return undefined;
  }
  const total = Object.values(durations).reduce(
    (sum, seconds) => sum + (toFiniteNumber(seconds) ?? 0),
    0
  );
  return toPercent(toFiniteNumber(durations[reason]) ?? 0, total);
};

const getStationMetrics = (
  summary: CallStats,
  callDurationSeconds: number
): StationDeviceMetrics => {
  const durations = summary.outboundRtpVideo?.qualityLimitationDurations;
  const framesDecoded = toFiniteNumber(summary.inboundRtpVideo?.framesDecoded);
  const totalDecodeTime = toFiniteNumber(
    summary.inboundRtpVideo?.totalDecodeTime
  );

  return {
    cpuLimitedPercent: getLimitationPercent(durations, 'cpu'),
    bandwidthLimitedPercent: getLimitationPercent(durations, 'bandwidth'),
    avgDecodeTimeMs:
      framesDecoded && totalDecodeTime !== undefined
        ? roundToTwoDecimals((totalDecodeTime / framesDecoded) * 1000)
        : undefined,
    cameraFps: toFiniteNumber(summary.outboundRtpVideo?.sourceFramesPerSecond),
    keyframeRequestsPerMinute: perMinute(
      getKeyframeRequestCount(summary),
      callDurationSeconds
    ),
    localCandidateType: summary.selectedCandidatePair?.localCandidateType,
    localRelayProtocol: summary.selectedCandidatePair?.localRelayProtocol,
  };
};

const isDegraded = (path: DirectionalPathQuality): boolean =>
  path.score !== undefined && path.score < THRESHOLDS.degradedPathScore;

const isAtLeast = (value: number | undefined, threshold: number): boolean =>
  value !== undefined && value >= threshold;

const isLowFps = (fps: number | undefined): boolean =>
  fps !== undefined && fps > 0 && fps < THRESHOLDS.lowFps;

const resolveLikelySource = (
  stationFactors: StationQualityFactor[],
  endpointFactors: EndpointQualityFactor[],
  { received, sent }: CallDirectionScores
): QualityFaultSource => {
  if (stationFactors.length > 0 && endpointFactors.length > 0) {
    return 'both';
  }
  if (stationFactors.length > 0) {
    return 'station';
  }
  if (endpointFactors.length > 0) {
    return 'endpoint';
  }

  // Degradation seen on only one measurable direction can't be split between sides.
  const hasUnattributedDegradation = isDegraded(received) || isDegraded(sent);
  const noPathMeasured =
    received.score === undefined && sent.score === undefined;

  return hasUnattributedDegradation || noPathMeasured
    ? 'indeterminate'
    : 'none';
};

// Factor counts are not compared: station signals are far more observable and often share one root cause.
const resolvePrimarySource = (
  likelySource: QualityFaultSource,
  stationFactors: StationQualityFactor[],
  { received, sent }: CallDirectionScores
): QualityFaultSource => {
  if (likelySource !== 'both') {
    return likelySource;
  }
  if (stationFactors.some((factor) => STATION_OVERLOAD_FACTORS.has(factor))) {
    return 'station';
  }
  if (received.score === undefined || sent.score === undefined) {
    return 'both';
  }

  const scoreGap = received.score - sent.score;
  if (scoreGap <= -THRESHOLDS.primaryDirectionScoreMargin) {
    return 'endpoint';
  }
  if (scoreGap >= THRESHOLDS.primaryDirectionScoreMargin) {
    return 'station';
  }
  return 'both';
};

/**
 * Estimates which side of the call caused degraded quality.
 *
 * Each media direction is attributed to its sender's uplink (the usual constrained link),
 * so this is a per-call heuristic; aggregating across calls confirms a station or endpoint.
 */
export const attributeCallQuality = (
  summary: CallStats,
  directions: CallDirectionScores = scoreCallDirections(summary)
): CallQualityAttribution => {
  const { received, sent } = directions;
  const callDurationSeconds = getCallDurationSeconds(summary);
  const station = getStationMetrics(summary, callDurationSeconds);

  const receivedDegraded = isDegraded(received);
  const sentDegraded = isDegraded(sent);
  const bothPathsMeasured =
    received.score !== undefined && sent.score !== undefined;
  const slowDecode = isAtLeast(
    station.avgDecodeTimeMs,
    THRESHOLDS.avgDecodeTimeMs
  );
  const cpuLimited = isAtLeast(
    station.cpuLimitedPercent,
    THRESHOLDS.cpuLimitedPercent
  );
  const frequentFreezes = isAtLeast(
    perMinute(
      toFiniteNumber(summary.inboundRtpVideo?.freezeCount),
      callDurationSeconds
    ),
    THRESHOLDS.freezesPerMinute
  );
  const sentVideoDisrupted =
    isAtLeast(
      getKeyframeRequestCount(summary),
      THRESHOLDS.minKeyframeRequests
    ) &&
    isAtLeast(
      station.keyframeRequestsPerMinute,
      THRESHOLDS.keyframeRequestsPerMinute
    );

  const stationFactors: StationQualityFactor[] = [];
  const endpointFactors: EndpointQualityFactor[] = [];

  if (bothPathsMeasured && sentDegraded) {
    stationFactors.push('sent_path_degraded');
  }
  if (sentVideoDisrupted) {
    stationFactors.push('sent_video_disruptions');
  }
  // Freezes with a struggling local machine are a decode/render problem here, not the network.
  if (frequentFreezes && (slowDecode || cpuLimited)) {
    stationFactors.push('received_freezes');
  }
  if (cpuLimited) {
    stationFactors.push('cpu_limited');
  }
  if (
    isAtLeast(
      station.bandwidthLimitedPercent,
      THRESHOLDS.bandwidthLimitedPercent
    )
  ) {
    stationFactors.push('bandwidth_limited');
  }
  if (slowDecode) {
    stationFactors.push('slow_decode');
  }
  if (isLowFps(station.cameraFps)) {
    stationFactors.push('low_camera_framerate');
  }
  // TURN over TCP/TLS adds head-of-line blocking; only relevant when a path is actually degraded.
  if (
    (receivedDegraded || sentDegraded) &&
    station.localCandidateType === 'relay' &&
    (station.localRelayProtocol === 'tcp' ||
      station.localRelayProtocol === 'tls')
  ) {
    stationFactors.push('tcp_relay');
  }

  if (bothPathsMeasured && receivedDegraded) {
    endpointFactors.push('received_path_degraded');
  }
  if (frequentFreezes && !slowDecode && !cpuLimited) {
    endpointFactors.push('received_freezes');
  }
  if (
    isLowFps(toFiniteNumber(summary.inboundRtpVideo?.framesPerSecond)) &&
    !receivedDegraded &&
    !slowDecode &&
    !cpuLimited
  ) {
    endpointFactors.push('low_received_framerate');
  }

  const likelySource = resolveLikelySource(
    stationFactors,
    endpointFactors,
    directions
  );

  return {
    likelySource,
    primarySource: resolvePrimarySource(
      likelySource,
      stationFactors,
      directions
    ),
    stationFactors,
    endpointFactors,
    station,
  };
};
